import { FC, useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import useVideoStore from '@/stores/videoStore';
import { useVideoCommentStore } from '@/stores/videoCommentStore';
import { formatTime } from '@/helpers/formatTime';
import { useNoteComments } from '../hooks/useNoteComments';

interface CommentTimelineMarkersProps {
  noteId?: string;
  shareToken?: string | null;
}

/** Dots on the video scrubber at each timestamped (top-level) comment. */
const CommentTimelineMarkers: FC<CommentTimelineMarkersProps> = ({ noteId, shareToken }) => {
  const { videoRef, setVideoCurrentTime, setProgressCurrentTime, setProgressTime } = useVideoStore();
  const setHighlightedCommentId = useVideoCommentStore((s) => s.setHighlightedCommentId);
  const { data: comments } = useNoteComments({ noteId, shareToken });
  const [duration, setDuration] = useState(videoRef?.current?.duration ?? 0);

  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;
    const update = () => setDuration(video.duration || 0);
    update();
    video.addEventListener('loadedmetadata', update);
    video.addEventListener('durationchange', update);
    return () => {
      video.removeEventListener('loadedmetadata', update);
      video.removeEventListener('durationchange', update);
    };
  }, [videoRef]);

  if (!comments || !duration) return null;

  const timedComments = comments.filter((c) => c.video_time !== null && c.video_time >= 0);

  return (
    <>
      {timedComments.map((comment) => {
        const time = comment.video_time as number;
        const left = Math.min(Math.max((time / duration) * 100, 0), 99);
        return (
          <Tooltip key={comment.id} title={formatTime(time)} arrow>
            <Box
              sx={{
                position: 'absolute',
                left: `${left}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                cursor: 'pointer',
                zIndex: 10,
                transition: 'transform 0.15s',
                '&:hover': { transform: 'translate(-50%, -50%) scale(1.3)' },
              }}
              onClick={() => {
                setVideoCurrentTime(time);
                setProgressCurrentTime(time);
                setProgressTime();
                setHighlightedCommentId(comment.id);
              }}
            />
          </Tooltip>
        );
      })}
    </>
  );
};

export default CommentTimelineMarkers;
